import { useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { yup } from '../../../../services/yup';
import { Button } from '../../../common/button-new';
import { TextField } from '../../../common/form/text-field';
import { Form } from '../../../common';
import { usePhoneNumberValidation } from '../../../../hooks';
import { usePasswordValidation } from '../../../../hooks/use-password-validation';
import { formatPhoneNumber } from '../../../../utils';
import { ILoginFormProps } from './login-form.models';
import { useStyles } from './login-form.styles';
import { InternalLinks } from '../../../../constants';

export const LoginForm = ({
  onSubmit, isRegistration = true,
}: ILoginFormProps): JSX.Element => {
  const classes = useStyles();
  const intl = useIntl();
  const [searchParams] = useSearchParams();

  const prefilledUsername = useMemo(() => {
    // formatPhoneNumber returns '+7XXXXXXXXXX'; drop '+' to match the tel TextField's unmasked form.
    const formatted = formatPhoneNumber(searchParams.get('phone') ?? undefined);
    return formatted ? formatted.slice(1) : '';
  }, [searchParams]);
  const isPhonePrefilled = !!prefilledUsername;

  const defaultValues = useMemo(() => ({
    username: prefilledUsername,
    password: '',
  }), [prefilledUsername]);

  const phoneNumberValidation = usePhoneNumberValidation();
  const passwordValidation = usePasswordValidation();

  const validationSchema = useMemo(() => yup.object({
    username: phoneNumberValidation,
    password: passwordValidation,
  }), [passwordValidation, phoneNumberValidation]);

  return (
    <Form
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      validationSchema={validationSchema}
    >
      <TextField
        autoFocus={!isPhonePrefilled}
        name="username"
        type="tel"
        fullWidth
        label={(
          <FormattedMessage
            id="login.phoneTextField.label"
          />
        )}
      />
      <TextField
        autoFocus={isPhonePrefilled}
        name="password"
        type="password"
        fullWidth
        label={(
          <FormattedMessage
            id="login.passwordTextField.label"
          />
        )}
        placeholder={intl.formatMessage({ id: 'login.passwordTextField.placeholder' })}
        footerActionProps={{
          style: 'text',
          className: classes.resetLink,
          testId: 'password-forget-link', // Don't remove data-testid, it is used by Yandex Metrica
          to: InternalLinks.resetPassword,
          children: (
            <FormattedMessage
              id="login.resetPasswordLink.label"
            />
          ),
        }}
      />
      <div className={classes.actions}>
        <Button
          type="submit"
          style="dark"
          testId="login-submit" // Don't remove data-testid, it is used by Yandex Metrica
        >
          <FormattedMessage
            id="login.loginButton.label"
          />
        </Button>
        {isRegistration && (
          <Button
            testId="register-link" // Don't remove data-testid, it is used by Yandex Metrica
            style="outlined"
            to={InternalLinks.registration}
          >
            <FormattedMessage id="login.registrationButton.label" />
          </Button>
        )}
      </div>
    </Form>
  );
};
